import React, {useEffect, useState} from 'react';
import {LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from 'recharts';
import PVData from '../../../../resources/Rivian/pv_battery_data.json';
import {
    Checkbox,
    Stack,
    Flex,
    useColorModeValue,
    Text,
    Spacer,
    IconButton, Box
} from "@chakra-ui/react";
import {FiMaximize2} from "react-icons/fi";
import ChartModal from "../../../../components/Workspace/ResultsTab/ChartModal";
import RangeComponent from "../../../../components/Workspace/ResultsTab/RangeComponent";

const BatteryChart = () => {
    const pvData = PVData["data"];
    const labels = PVData["labels"];
    const [activeLabels, setActiveLabels] = useState(labels);
    const [data, setData] = useState(pvData);
    const [showGrid, setShowGrid] = useState(true);
    const [xAxisInterval, setXAxisInterval] = useState(288);
    const [xAxisLabel, setXAxisLabel] = useState("days");

    const colors = [
        useColorModeValue("#8884d8", "#8884d8"),
        useColorModeValue("#82ca9d", "#82ca9d"),
        useColorModeValue("#ffc658", "#ffc658")
    ]

    useEffect(() => {
        setXAxisInterval(getXAxisInterval(data));
        setXAxisLabel(getXAxisLabel(data));
    }, [data]);

    const maxDays = Math.floor(pvData.length/(288)) - 1;

    const formatXAxis = (tickItem, currentData) => {
        if (currentData.length > 288)
            return Math.floor((tickItem * 5) / (60*24)) ;
        else if (currentData.length <= 288)
            return (Math.floor(tickItem/12) % (24));
    };

    const getXAxisInterval = (currentData) => {
        if (currentData.length > 288)
            return 288;
        else
            return 12;
    }

    const getXAxisLabel = (currentData) => {
        if (currentData.length > 288)
            return "Days";

        else
            return "Hours"
    }

    const changeChecked = (label, val) => {
        if (val)
            setActiveLabels([...activeLabels, label]);
        else
            setActiveLabels(activeLabels.filter(value => value !== label));
    }


    const onLegendClick = (data, index, event) => {

        if (data && data.hasOwnProperty("dataKey")) {
            let label = data["dataKey"];
            changeChecked(label, data.inactive);

        }
    }

    const onRangeSave = (activeTab, dayData, hourData) => {

        let lowerVal = 0;
        let higherVal = data.length;

        if (activeTab === 0) {
            const {dayStart, dayEnd} = dayData;
            lowerVal = dayStart*288;
            higherVal = dayEnd*288 - 1;

        } else if (activeTab === 1) {
            const { selectedDay, hourStart, hourEnd } = hourData;

            lowerVal = selectedDay*288 + hourStart*12;
            higherVal = selectedDay*288 + (hourEnd + 1)*12 - 1;
        }

        let newData = pvData.filter(record => (record["Time Interval"] >= lowerVal && record["Time Interval"] <= higherVal));

        setXAxisInterval(getXAxisInterval(newData));
        setXAxisLabel(getXAxisLabel(newData));
        setData(newData);
    }


    return (

        <Stack direction={"row"} height={"100%"} width={"100%"} py={5} >
            <ResponsiveContainer height={"100%"} minHeight={"100%"} >
                <LineChart data={data} margin={{top: 5, right: 20, bottom: 5, left: 0}}>
                    {labels.map((label, index) => {
                        return <Line key={index} type="monotone" dataKey={label} stroke={colors[index]} dot={false}
                                     hide={!activeLabels.includes(label)}/>
                    })}
                    {showGrid && <CartesianGrid stroke="#ccc"/>}
                    <XAxis
                        dataKey="Time Interval"
                        tickFormatter={(tickItem) => (formatXAxis(tickItem, data))}
                        interval={xAxisInterval}
                        label={{ value: xAxisLabel, position: 'insideBottom', offset: -10 }}/>
                    <YAxis label={{ value: 'MW', angle: -90, position: 'insideLeft' }}/>
                    <Tooltip/>

                    <Legend wrapperStyle={{ bottom: -10 }} onClick={onLegendClick}/>
                </LineChart>
            </ResponsiveContainer>
            <Flex direction={"column"} gap={4} >
                <Stack spacing={[1, 2]} direction={['column']}>
                    <Text fontWeight={"semibold"}>Outputs</Text>
                    {labels.map((label, idx) => (
                        <Checkbox
                            key={idx}
                            color={colors[idx]}
                            value={label}
                            isChecked={activeLabels.includes(label)}
                            onChange={(e) => {
                                changeChecked(label, e.target.checked)
                            }}
                        >
                            {label}
                        </Checkbox>
                    ))}
                </Stack>

                <Stack spacing={[1, 2]} direction={['column']} width={"100%"}>
                    <Stack direction={['row']} align={"center"} gap={4}>
                        <Text fontWeight={"semibold"}>Time Range</Text>
                    </Stack>
                    <RangeComponent maxDayRange={maxDays} onRangeSave={onRangeSave} />
                    <Checkbox pt={5} value={"Show Grid"} isChecked={showGrid}
                              onChange={e => setShowGrid(e.target.checked)}>Show Grid</Checkbox>

                </Stack>
            </Flex>
        </Stack>
    );
};

const BatteryDescription = () => (
    <Box mt={"-35px"}  >
        <Flex gap={1} textColor={useColorModeValue("#ffc658", "#ffc658")}><Text fontWeight={"bold"}>Battery size:</Text><Text> 20 MW/16 MW-Hr</Text></Flex>
        <Flex gap={1} textColor={useColorModeValue("#82ca9d", "#82ca9d")}><Text fontWeight={"bold"}>PV Size:</Text> <Text>95 MW DC</Text></Flex>
    </Box>
)
const BatteryComponent = (props) => {
    const [modalOpen, setModalOpen] = useState(false);

    const onModalOpen = () => {
        setModalOpen(true);
    }
    const onModalClose = () => {
        setModalOpen(false);
    }
    return (
        <Flex direction={"column"} {...props}>
            <Stack direction={"row"} align={"center"} justify={"center"} w={"100%"}>
                <Text fontWeight={"bold"} flex={1} align={"center"} fontSize={"xl"}>Battery & PV Chart</Text>
                <IconButton aria-label='Search database' icon={<FiMaximize2/>} onClick={onModalOpen} />
            </Stack>
            <BatteryDescription/>
            <BatteryChart />
            <ChartModal onClose={onModalClose} isOpen={modalOpen} description={<BatteryDescription/>} chart={<BatteryChart/>} label={"Battery & PV Chart"}/>
        </Flex>
    )
}


export {BatteryComponent, BatteryChart};
